import { Component } from '@theme/component';
import { wishlistItems } from '@theme/bt-wishlist';

const CARD_BATCH = 20;

/**
 * Wishlist page — Swym list + Liquid product cards.
 *
 * @extends {Component}
 */
class BtWishlistPage extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    document.addEventListener('wishlist:state', this.#onWishlistState, { signal: this.#abort.signal });
    void this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  async #load() {
    if (this.dataset.previewEmpty === 'true') {
      this.#showEmpty();
      return;
    }

    try {
      const items = await wishlistItems();
      if (!this.isConnected) return;

      if (!items?.length) {
        this.#showEmpty();
        return;
      }

      const unique = uniqueWishlistProducts(items);
      const cards = await fetchCardBatches(
        unique.map((item) => item.handle),
        this.#abort?.signal
      );
      if (!this.isConnected) return;

      const grid = this.refs.grid;
      if (!(grid instanceof HTMLElement)) return;

      const fragment = document.createDocumentFragment();
      for (const item of unique) {
        const card = cards.get(item.handle);
        if (!card) continue;
        fragment.append(wrapCard(card, item));
      }

      if (!fragment.childNodes.length) {
        this.#showEmpty();
        return;
      }

      grid.replaceChildren(fragment);
      this.#showGrid();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (this.isConnected) this.#showEmpty();
    }
  }

  #showGrid() {
    this.#setHidden(this.refs.loading, true);
    this.#setHidden(this.refs.empty, true);
    this.#setHidden(this.refs.grid, false);
  }

  #showEmpty() {
    this.#setHidden(this.refs.loading, true);
    this.#setHidden(this.refs.grid, true);
    this.#setHidden(this.refs.empty, false);
    if (this.refs.grid instanceof HTMLElement) this.refs.grid.replaceChildren();
  }

  /**
   * @param {Event} event
   */
  #onWishlistState = (event) => {
    const detail = event instanceof CustomEvent && event.detail && typeof event.detail === 'object' ? event.detail : {};
    if (detail.active !== false) return;

    const productId = String(detail.productId || '');
    if (!productId) return;

    const grid = this.refs.grid;
    if (!(grid instanceof HTMLElement)) return;

    for (const row of grid.querySelectorAll(`[data-product-id="${cssEscape(productId)}"]`)) {
      row.remove();
    }

    if (!grid.querySelector('[data-product-id]')) this.#showEmpty();
  };

  /**
   * @param {Element | Element[] | undefined} node
   * @param {boolean} hidden
   */
  #setHidden(node, hidden) {
    const el = Array.isArray(node) ? node[0] : node;
    if (el instanceof HTMLElement) el.hidden = hidden;
  }
}

/**
 * @param {{ empi?: number, epi?: number, du?: string }} item
 */
function productHandle(item) {
  const fromUrl = handleFromUrl(item?.du);
  if (fromUrl) return fromUrl;
  return '';
}

/**
 * @param {string | undefined} value
 */
function handleFromUrl(value) {
  if (!value) return '';
  try {
    const url = new URL(value, window.location.origin);
    const match = url.pathname.match(/\/products\/([^/]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

/**
 * @param {Array<Record<string, unknown>>} items
 */
function uniqueWishlistProducts(items) {
  const seen = new Set();
  /** @type {{ handle: string, productId: string, variantId: string }[]} */
  const unique = [];

  for (const item of items) {
    const handle = productHandle(item);
    const productId = String(item?.empi || '');
    if (!handle || !productId || seen.has(productId)) continue;
    seen.add(productId);
    unique.push({
      handle,
      productId,
      variantId: item?.epi != null ? String(item.epi) : '',
    });
  }

  return unique;
}

/**
 * @param {string[]} handles
 * @param {AbortSignal | undefined} signal
 */
async function fetchCardBatches(handles, signal) {
  /** @type {Map<string, Element>} */
  const cards = new Map();
  const unique = [...new Set(handles.filter(Boolean))];

  for (let index = 0; index < unique.length; index += CARD_BATCH) {
    const batch = unique.slice(index, index + CARD_BATCH);
    const next = await fetchCardBatch(batch, signal);
    for (const [handle, card] of next) cards.set(handle, card);
  }

  const missing = unique.filter((handle) => !cards.has(handle));
  await Promise.all(
    missing.map(async (handle) => {
      const card = await fetchCard(handle, signal);
      if (card) cards.set(handle, card);
    })
  );

  return cards;
}

/**
 * @param {string[]} handles
 * @param {AbortSignal | undefined} signal
 */
async function fetchCardBatch(handles, signal) {
  /** @type {Map<string, Element>} */
  const cards = new Map();
  if (!handles.length) return cards;

  const root = window.Shopify?.routes?.root || '/';
  const url = `${root}search?q=${encodeURIComponent(handles.join(','))}&type=product&view=bt-product-cards`;
  const response = await fetch(url, { signal });
  if (!response.ok) return cards;

  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  for (const wrap of doc.querySelectorAll('[data-product-handle]')) {
    if (!(wrap instanceof HTMLElement)) continue;
    const handle = wrap.dataset.productHandle;
    const card = wrap.querySelector('product-card');
    if (handle && card) cards.set(handle, card);
  }

  return cards;
}

/**
 * @param {string} handle
 * @param {AbortSignal | undefined} signal
 */
async function fetchCard(handle, signal) {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=collection-card`, { signal });
  if (!response.ok) return null;
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  return doc.querySelector('product-card');
}

/**
 * @param {Element} card
 * @param {{ handle: string, productId: string, variantId: string }} item
 */
function wrapCard(card, item) {
  const row = document.createElement('li');
  row.className = 'bt-plp__item';
  row.dataset.productId = item.productId;
  row.dataset.productHandle = item.handle;
  const node = /** @type {Element} */ (card.cloneNode(true));
  if (node instanceof HTMLElement) {
    node.dataset.productId = item.productId;
    if (item.variantId) {
      const wishlist = node.querySelector('.product-card__wishlist');
      if (wishlist instanceof HTMLElement) wishlist.dataset.variantId = item.variantId;
    }
  }
  row.append(node);
  return row;
}

/**
 * @param {string} value
 */
function cssEscape(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/"/g, '\\"');
}

if (!customElements.get('bt-wishlist-page')) {
  customElements.define('bt-wishlist-page', BtWishlistPage);
}
